import React, { useState, useEffect, useRef, ReactNode, TouchEvent } from 'react';
import HomeFeaturedMovie from "../HomeFeaturedMovie";
import styled from 'styled-components';
import ScrollToMoviesButton from './ScrollToMoviesButton';
import { breakpoints } from '../../utils/styles';
import FeaturedMoviesPromo from './FeaturedMoviesPromo';

const Arrow = styled.button<{ left?: boolean }>`
    display: none;

    @media screen and (min-width: ${breakpoints.tablet}px) {
        background: none;
        border: none;
        color: white;
        cursor: pointer;
        display: block;
        font-size: 2rem;
        outline: none;
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        z-index: 1;

        &:focus { outline: none; }

        ${props => props.left ? 'left: 10px;' : 'right: 10px;'}
    }
`;

const Dots = styled.div`
    position: absolute;
    bottom: 1.5rem;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    gap: 5px;
    z-index: 1;

    em {
        display: inline-block;
        color: white;
        cursor: pointer;
        font-size: 0.55rem;
        margin: 0 4px;
    }
`;

interface Props {
    hidden: boolean;
    movies: any[];
    movieTabOpen: boolean;
    promos: any[];
    onMovieSelect: (movie: any) => void;
}

function FeaturedMoviesCarousel(props: Props) {
    const items = getSortedItems(props.movies, props.promos);
    const total = items.length;

    if (total === 0) return null;

    const [index, setIndex] = useState(0);
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    const startXRef = useRef<number | null>(null);
    const moveXRef = useRef<number | null>(null);

    const goToNext = () => setIndex((prev) => (prev + 1) % total);
    const goToPrev = () => setIndex((prev) => (prev - 1 + total) % total);

    useEffect(() => {
        intervalRef.current = setInterval(goToNext, 10000);
        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, []);

    const resetInterval = () => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        intervalRef.current = setInterval(goToNext, 10000);
    };

    const handleTouchStart = (e: TouchEvent<HTMLDivElement>) => {
        startXRef.current = e.touches[0].clientX;
        moveXRef.current = null;
    };

    const handleTouchMove = (e: TouchEvent<HTMLDivElement>) => {
        moveXRef.current = e.touches[0].clientX;
    };

    const handleTouchEnd = () => {
        if (startXRef.current === null || moveXRef.current === null) return;

        const deltaX = moveXRef.current - startXRef.current;
        const threshold = 50;

        if (Math.abs(deltaX) > threshold) {
            resetInterval();
            if (deltaX > 0) {
                goToPrev();
            } else {
                goToNext();
            }
        }

        startXRef.current = null;
        moveXRef.current = null;
    };
    
    return <div id='home-feat-movie'>
        <div
            style={{ position: 'relative', overflow: 'hidden', width: '100%' }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
        >
            <div style={{ display: 'flex', transition: 'transform 0.5s ease', transform: `translateX(-${index * 100 / total}%)`, width: `${total * 100}%` }}>
                {items.map((item, i) => (
                    item.type === 'movie'
                        ? <HomeFeaturedMovie key={i} movie={item.item} movieTabOpen={props.movieTabOpen} onMovieSelect={props.onMovieSelect} hidden={props.hidden} />
                        : <FeaturedMoviesPromo promo={item.item} key={i} isCurrent={index === i} />
                ))}
            </div>

            {total > 1 && <>
                <Arrow left
                    onClick={() => {
                        resetInterval();
                        goToPrev();
                    }}
                >
                    <em className='fal fa-chevron-left'></em>
                </Arrow>

                <Arrow
                    onClick={() => {
                        resetInterval();
                        goToNext();
                    }}
                >
                    <em className='fal fa-chevron-right'></em>
                </Arrow>

                <Dots>
                    {Array.from({ length: total }, (_, i) => (
                        <em className={`${index === i ? 'fa-solid' : 'fal'} fa-circle`} onClick={() => { resetInterval(); setIndex(i); }} key={i} />
                    ))}
                </Dots>
            </>}
        </div>
        <ScrollToMoviesButton/>
    </div>;
}

function getSortedItems(movies: any[], promos: any[]): any[] {
    const items: { item: any, type: string }[] = movies.map(movie => ({ item: movie, type: 'movie' }))
        .concat(promos.map(promo => ({ item: promo, type: 'promo' })));
    
    items.sort((a, b) => {
        if (('position' in a.item && 'position' in b.item)) {
            return b.item.position - a.item.position;
        }
        return 0;
    });

    return items;
}

export default FeaturedMoviesCarousel;